import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { NavigationStart, Router } from '@angular/router'
import Flow from '@flowjs/flow.js'
import type { FlowFile } from 'flowjs'
import { MediaRootDetail, MediaRootPermission } from 'elements-sdk'
import { Subject } from 'rxjs'
import { auditTime } from 'rxjs/operators'

import { APIService } from 'api/module'
import { PageTitleService } from 'common/util/services/pageTitle.service'
import { Logger } from 'common/log'
import { BeforeUnloadService } from 'main/services/beforeUnload.service'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { MessageBoxService } from 'common/util/module'
import { NotifyService } from 'common/util/services/notify.service'
import { TasksService } from 'tasks/module'

export enum UploadState {
  IDLE = 0,
  PENDING = 1,
  UPLOADING = 2,
  PAUSED = 3,
  DONE = 4,
}

interface FlowFileExtras {
  fullPath: string
  path?: string
  pending?: boolean
  finishing?: boolean
  done?: boolean
}

export type File = FlowFile & FlowFileExtras

@Injectable({ providedIn: 'root' })
export class UploaderService {
  readonly state = UploadState

  private logger = new Logger(__filename)

  uploaded$ = new Subject<File>()
  filesAdded$ = new Subject<File[]>()
  complete$ = new Subject<void>()

  active = false
  flow: Flow
  path: string|null = null
  permission: MediaRootPermission|null = null
  mediaRoot: MediaRootDetail|null = null
  showDragHint = false

  constructor (
    private api: APIService,
    private beforeUnload: BeforeUnloadService,
    private translate: TranslateService,
    private pageTitle: PageTitleService,
    private mediaPermissions: MediaPermissionsService,
    private messageBox: MessageBoxService,
    private notify: NotifyService,
    private tasks: TasksService,
    router: Router,
  ) {
    this.flow = new Flow({
      allowDuplicateUploads: true,
      chunkSize: 1024 * 1024 * 10,
      target: '/api/2/uploads/chunk',
      generateUniqueIdentifier: (file: File) => {
        // Some confusion in different versions of Firefox
        const relativePath = file.relativePath || file['webkitRelativePath'] || file['fileName'] || file.name
        const ts = Math.floor(Date.now())
        return `${file.size}-${relativePath.replace(/[^\w-]/gim, '')}-${ts}`
      },
    })
    this.flow.on('fileProgress', this.onFileProgress.bind(this))
    this.flow.on('complete', this.onComplete.bind(this))
    this.flow.on('fileSuccess', this.onFileUploaded.bind(this))
    this.flow.on('filesSubmitted', this.onFilesAdded.bind(this))

    const dragState = new Subject<boolean>()
    dragState.pipe(auditTime(100)).subscribe(v => {
      this.showDragHint = v
    })
    document.body.addEventListener('dragover', () => {
      dragState.next(this.active)
    })
    document.body.addEventListener('dragleave', () => {
      dragState.next(false)
    })
    document.body.addEventListener('drop', () => {
      dragState.next(false)
    })

    router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.deactivate()
      }
    })
  }

  getState (): UploadState {
    if (!this.flow.files.length) {
      return UploadState.IDLE
    }

    if (this.flow.files.some(x => x.paused)) {
      return UploadState.PAUSED
    }

    if (this.flow.files.some(x => x.isUploading())) {
      return UploadState.UPLOADING
    }

    return UploadState.DONE
  }

  getProgress (): number {
    if (![UploadState.UPLOADING, UploadState.PAUSED].includes(this.getState())) {
      return 1.0
    }
    let sum = 0
    for (const file of this.flow.files) {
      sum += file.progress()
    }
    return sum / (this.flow.files.length || 1)
  }

  getPageTitle (): string|null {
    const state = this.getState()
    if (![UploadState.UPLOADING, UploadState.PAUSED].includes(state)) {
      return null
    }

    return `[${Math.round(this.getProgress() * 100)}%${state === UploadState.PAUSED ? ' PAUSED' : ''}] `
  }

  activate (path: string, mediaRoot: MediaRootDetail): void {
    if (!this.active) {
      this.flow.assignDrop(document.body)
    }
    this.active = true
    this.path = path
    this.mediaRoot = mediaRoot
    this.permission = this.mediaPermissions.getPermissionForPath(path)
  }

  deactivate (): void {
    this.active = false
    this.flow.unAssignDrop(document.body)
  }

  clear (): void {
    for (const file of [...this.flow.files]) {
      try {
        file.cancel()
      } catch (error) {}
    }

    this.flow.files.length = 0
  }

  async onFilesAdded (files: File[]): Promise<void> {
    if (!this.path) {
      return
    }

    const path = this.path
    const promises = files.map(async file => {
      file.path = path
      file.pending = true
      file.pause()
      file.fullPath = file.path + '/' + file.relativePath

      await this.api.main.registerUpload({
        registerUploadEndpointRequest: {
          uploadId: file.uniqueIdentifier,
          path: file.fullPath,
        },
      })
      return file
    })

    const results = await Promise.all(promises)
    this.filesAdded$.next(results)
  }

  async onFileUploaded (file: File): Promise<void> {
    file.finishing = true
    try {
      const task = await this.api.main.finishUpload({
        finishUploadEndpointRequest: {
          uploadId: file.uniqueIdentifier,
        },
      })
      await this.tasks.waitTasksComplete([task.id])
      file.done = true
      this.notify.info(this.translate.instant('Upload complete'))
    } catch (error) {
      file.error = error
      this.notify.errorFromResponse(this.translate.instant('Upload failed'), error)
      return
    } finally {
      file.finishing = false
      this.updatePageTitle()
    }

    this.uploaded$.next(file)

    if (/.zip/i.test(file.name)) {
      // Wait for possible route changes that would close the modal
      setTimeout(async () => {
        try {
          await this.messageBox.show({
            title: 'ZIP',
            text: this.translate.instant('Unpack this zip file?'),
            positive: this.translate.instant('Unpack'),
            negative: this.translate.instant('Don\'t unpack'),
          })
        } catch {
          return
        }
        this.api.storage.unzipFile({
          fileUnzipEndpointRequest: {
            input: `${this.path}/${file.name}`,
            remove: true,
          },
        })
      }, 500)
    }
  }

  onFileProgress (): void {
    this.updatePageTitle()
    if (this.getState() === UploadState.UPLOADING) {
      this.beforeUnload.preventUnload(this.translate.instant('Uploads are running. Leave?'))
    }
  }

  async onComplete (): Promise<void> {
    this.updatePageTitle()
    this.beforeUnload.allowUnload()
    this.complete$.next()
  }

  private updatePageTitle (): void {
    this.pageTitle.setPrefix(this.getPageTitle())
  }
}
